// Shared HTTP helpers for widget → backend calls.
//
// Plain fetch — no axios — to keep the bundle small. Every helper
// normalises errors to `Error` instances with a usable `.message` so
// the UI can render them without knowing whether the failure was a
// network error or an HTTP 4xx.

/**
 * Build a fully-qualified URL by joining the embedder-configured base
 * with a relative path. Tolerates trailing slashes on the base and
 * leading slashes on the path so embedders don't have to think about
 * which side owns the separator.
 */
export function joinUrl(base, pathPart) {
    if (!base) return pathPart
    const b = String(base).replace(/\/+$/, '')
    const p = String(pathPart).replace(/^\/+/, '')
    return `${b}/${p}`
}

/**
 * POST a FormData body to the backend.
 *
 * @param {string} url - absolute URL
 * @param {FormData} formData
 * @returns {Promise<object>} parsed JSON body
 *
 * Throws an Error with a `.message` pulled from the response body's
 * `message` field (matching the backend's `{ success, message }` shape)
 * or a generic fallback when the body isn't JSON.
 */
export async function postFormData(url, formData) {
    let res
    try {
        res = await fetch(url, {
            method: 'POST',
            body: formData,
            // Don't set Content-Type — the browser must add the
            // multipart boundary parameter, which it only does when
            // we leave the header alone.
            mode: 'cors',
            credentials: 'omit',
        })
    } catch (networkErr) {
        // CORS rejections and offline / DNS failures end up here.
        // The message from `fetch` is famously unhelpful (often just
        // "Failed to fetch") so we surface a more actionable one.
        throw new Error(
            'Unable to reach the server. Check your connection and try again.'
        )
    }

    // Try to parse JSON either way — most error responses still carry
    // a `{ success: false, message: ... }` body we can show.
    let body = null
    try {
        body = await res.json()
    } catch {
        // Non-JSON response. Leave body as null; fall through to the
        // status-based fallback message below.
    }

    if (!res.ok) {
        const msg =
            (body && (body.message || body.error)) ||
            `Request failed (${res.status})`
        const err = new Error(msg)
        err.status = res.status
        err.body = body
        throw err
    }

    return body
}
